import { useState, useRef, useCallback } from "react";
import { useMutation } from "@apollo/client";

import { CREATE_QUIZ_SESSION, UPDATE_QUIZ_SESSION, COMPLETE_QUIZ_SESSION } from "../../../graphql/mutations/QuizSession";
import { QuizAnswerOption, QuizQuestion } from "../../../gql/graphql";

interface UseQuizSessionProps {
  configurationId: string;
  isPreviewMode: boolean;
}

export const useQuizSession = ({ configurationId, isPreviewMode }: UseQuizSessionProps) => {
  const [quizSessionId, setQuizSessionId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string | null>(null);
  const sessionInitializedRef = useRef(false);

  const [createQuizSession] = useMutation(CREATE_QUIZ_SESSION);
  const [updateQuizSession] = useMutation(UPDATE_QUIZ_SESSION);
  const [completeQuizSession] = useMutation(COMPLETE_QUIZ_SESSION);

  const initializeSession = useCallback(async () => {
    if (!isPreviewMode && !quizSessionId && !sessionInitializedRef.current) {
      sessionInitializedRef.current = true;
      const gameStartTime = new Date().toISOString();
      setStartTime(gameStartTime);
      
      try {
        const { data } = await createQuizSession({
          variables: {
            data: {
              quizConfigurationId: configurationId,
              nickname: null,
            },
          },
        });

        const sessionId = data.createQuizSession.id;
        setQuizSessionId(sessionId);
        return sessionId;
      } catch (error) {
        console.warn("Quiz session tracking not available:", error);
        sessionInitializedRef.current = false;
        return null;
      }
    }
    return quizSessionId;
  }, [createQuizSession, configurationId, isPreviewMode, quizSessionId]);

  const updateSession = useCallback(
    async (
      currentSessionId: string,
      questionIndex: number,
      currentQuestion: QuizQuestion,
      selectedOption: QuizAnswerOption
    ) => {
      if (!isPreviewMode && currentSessionId) {
        try {
          await updateQuizSession({
            variables: {
              data: {
                sessionId: currentSessionId,
                questionIndex,
                quizQuestionId: currentQuestion.id,
                quizAnswerOptionId: selectedOption.id,
              },
            },
          });
        } catch (error) {
          console.warn("Failed to update quiz session:", error);
        }
      }
    },
    [isPreviewMode, updateQuizSession]
  );

  const completeSession = useCallback(
    async (sessionId: string) => {
      if (sessionId) {
        try {
          await completeQuizSession({
            variables: {
              sessionId,
            },
          });
        } catch (error) {
          console.warn("Failed to complete quiz session:", error);
        }
      }
    },
    [completeQuizSession]
  );

  return {
    quizSessionId,
    startTime,
    initializeSession,
    updateSession,
    completeSession,
  };
};